import React, { useState, useEffect } from 'react';
import { ClipboardList, MapPin, History, CheckCircle, Clock, Package } from 'lucide-react';
import MessengerPendingView from '../components/MessengerPendingView';
import MessengerRoutePage from './MessengerRoutePage';
import MessengerHistoryView from '../components/MessengerHistoryView';
import api from '../services/api';
import toast from 'react-hot-toast';

const MessengerDashboard = () => {
    const [activeTab, setActiveTab] = useState('pending'); // pending, route, history
    const [pendingCounts, setPendingCounts] = useState({ to_accept: 0, to_route: 0 });
    const [hasActiveRoute, setHasActiveRoute] = useState(false);
    const [loading, setLoading] = useState(true);

    // Cargar contadores de pedidos pendientes
    const loadPendingCounts = async () => {
        try {
            const response = await api.get('/messenger/pending');
            if (response.data.success) {
                setPendingCounts({
                    to_accept: response.data.data.to_accept.length,
                    to_route: response.data.data.to_route.length
                });
            }
        } catch (error) {
            console.error('Error loading pending counts:', error);
        }
    };

    // Verificar si tiene ruta activa
    const checkActiveRoute = async () => {
        try {
            const response = await api.get('/routes/messenger/active');
            setHasActiveRoute(!!response.data.route);
        } catch (error) {
            // No active route is fine
            setHasActiveRoute(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPendingCounts();
        checkActiveRoute();
    }, []);

    // Reload counts when tab changes
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'pending') {
            loadPendingCounts();
        } else if (tab === 'route') {
            checkActiveRoute();
        }
    };

    const tabs = [
        {
            id: 'pending',
            label: 'Mis Pedidos',
            icon: Package,
            badge: pendingCounts.to_accept + pendingCounts.to_route,
            color: 'blue'
        },
        {
            id: 'route',
            label: 'Ruta Activa',
            icon: MapPin,
            badge: hasActiveRoute ? '●' : null,
            color: 'green'
        },
        {
            id: 'history',
            label: 'Historial',
            icon: History,
            badge: null,
            color: 'gray'
        }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 py-3">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <ClipboardList className="w-7 h-7 text-blue-600" />
                        Panel del Mensajero
                    </h1>
                </div>

                {/* Tab Navigation */}
                <div className="max-w-7xl mx-auto px-2">
                    <div className="flex w-full border-b border-gray-200">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`
                                        flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-1 py-3 font-bold text-[11px] sm:text-sm transition-all relative
                                        ${isActive
                                            ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/30'
                                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                        }
                                    `}
                                >
                                    <div className="relative">
                                        <Icon className={`${isActive ? 'w-5 h-5' : 'w-4 h-4 opacity-70'}`} />
                                        {/* Mobile Badge (dot) if simple badge is needed, otherwise full badge is below */}
                                        {tab.badge && typeof tab.badge === 'string' && (
                                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border border-white"></span>
                                        )}
                                    </div>
                                    <span className="truncate max-w-[65px] sm:max-w-none">{tab.label}</span>

                                    {/* Number Badge */}
                                    {typeof tab.badge === 'number' && tab.badge > 0 && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-black rounded-full bg-red-500 text-white min-w-[18px] text-center shadow-sm">
                                            {tab.badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {activeTab === 'pending' && (
                    <MessengerPendingView
                        onUpdate={() => {
                            loadPendingCounts();
                            checkActiveRoute();
                        }}
                    />
                )}

                {activeTab === 'route' && (
                    <MessengerRoutePage
                        embedded={true}
                        onRouteClose={() => {
                            setHasActiveRoute(false);
                            setActiveTab('pending');
                            toast.success('Ruta cerrada exitosamente');
                        }}
                    />
                )}

                {activeTab === 'history' && (
                    <MessengerHistoryView />
                )}
            </div>
        </div>
    );
};

export default MessengerDashboard;
